import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { pageFlowConfigurationSelector } from '@redux/slices/workflowSlice'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import {
  ModalOptions,
  FooterConfiguration,
  ViewName,
  ViewOptions,
  TooltipConfiguration,
  RxHelpGuideOptions,
} from '@typesApp/PageFlow'
import { ROUTES } from '@constants/index'
import { PageFlowUtil } from '@utils/pageFlowUtil'
import { InsurancePlanType } from '@typesApp/InsurancePlan'

type ViewConfig = {
  next: string
  previous: string
}

type UsePageFlowResult = {
  isReady: boolean
  defaultView: string
  loginRedirectView: string
  maxSteps: number
  showSteps: boolean
  modalOptions?: ModalOptions
  tooltip?: TooltipConfiguration
  benefitPlanConfiguration?: {
    displayOrder: InsurancePlanType[]
  }
  rxhelpGuideOptions?: RxHelpGuideOptions

  /**
   * @returns navigation configuration (next, previous) for this view
   */
  getViewConfig: (view: ViewName) => ViewConfig

  /**
   * @returns current step for the view, taking into account the dynamic insurance forms.
   * Note: insurance information view should NOT use this api
   */
  getCurrentStep: (view: ViewName) => number

  /**
   * @returns view to resume to for in progress claim record or null if not configured
   */
  getResumeToView: (claimRecord: ClaimRecord) => ViewName | null

  /**
   * @returns footer configuration (for cta styling) for this view
   */
  getFooterConfiguration: (view: ViewName) => FooterConfiguration | null

  /**
   * @returns additional options for this view
   */
  getOptions: (view: ViewName) => ViewOptions | null
}

export const usePageFlow = (): UsePageFlowResult => {
  const config = useSelector(pageFlowConfigurationSelector)

  const getCurrentStep = (view: ViewName): number => {
    if (!config?.steps) return 0
    return config.steps.indexOf(view) + (config?.stepsOffset || 0)
  }

  const getViewConfig = (view: ViewName): ViewConfig => {
    return {
      next: toRoutePath(config?.flow[view]?.next),
      previous: toRoutePath(config?.flow[view]?.previous),
    }
  }

  const toRoutePath = (pageRoute: ViewName | undefined): string => {
    return !pageRoute ? '' : pageRoute === 'intro' ? ROUTES.BASE : pageRoute
  }

  const getResumeToView = (claimRecord: ClaimRecord): ViewName | null => {
    const mapping = config?.resumeMapping
    if (!mapping) {
      return null
    }

    return PageFlowUtil.getResumeToView(config, claimRecord)
  }

  const getFooterConfiguration = (viewName: ViewName): FooterConfiguration | null => {
    if (!config?.footerConfiguration) {
      return null
    }

    const ctaOptions = config?.footerConfiguration[viewName] || config?.footerConfiguration['*']

    // set initial max width on link
    const maxWidthStyle = { sm: '200px ' }
    const result: FooterConfiguration = {
      ...ctaOptions,
      cancelCta: {
        ...ctaOptions?.cancelCta,
        sx: {
          maxWidth: ctaOptions?.cancelCta?.renderAs === 'link' ? maxWidthStyle : undefined,
          ...ctaOptions?.cancelCta?.sx,
        },
      },
      submitCta: {
        ...ctaOptions?.submitCta,
        sx: {
          maxWidth: ctaOptions?.submitCta?.renderAs === 'link' ? maxWidthStyle : undefined,
          ...ctaOptions?.submitCta?.sx,
        },
      },
    }

    return result
  }

  const getOptions = (view: ViewName): ViewOptions | null => (config?.options ? config.options[view] : null)

  const result = useMemo<UsePageFlowResult>(() => {
    return {
      isReady: Boolean(config),
      maxSteps: config?.maxSteps || 0,
      showSteps: config?.showSteps === undefined ? true : config.showSteps,
      defaultView: toRoutePath(config?.defaultView) || ROUTES.BASE,
      loginRedirectView: toRoutePath(config?.loginRedirectView) || ROUTES.BASE,
      modalOptions: config?.modalOptions,
      tooltip: config?.tooltip,
      rxhelpGuideOptions: config?.rxhelpGuide,
      benefitPlanConfiguration: config?.benefitPlanConfiguration,
      getViewConfig,
      getCurrentStep,
      getResumeToView,
      getFooterConfiguration,
      getOptions,
    }
  }, [config])

  return result
}
